import React, { useContext, useEffect, useCallback, useState } from "react";
import clsx from "clsx";
import { Theme, makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Button,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import {
  SummaryInprocess,
  SummaryComplete,
  SummaryInprocessLabel,
  SummaryCompleteLabel,
  ExportExcel,
} from "../material/icons";
import { DesignContext } from "../context/design";
import { useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import ReactExport from "react-data-export";
import {
  CableTypeList,
  PoleSizeList,
  LampPowerList,
  RelayTypeList,
  SwitchTypeList,
  MeterTypeList,
} from "../constant/equip-options";
import { IProject } from "../interface/project";
import {
  getProjectById,
  putEditProject,
  saveProjectFile,
} from "../apis/project";
import { MainContext } from "../context/main";
import projectComplete from "../constant/email-template/project-complete";
import { sendEmail, uploadFile } from "../apis/other";
import { LangContext } from "../context/lang";
import { getHeadUserList } from "../apis/user";
import { makeBlobFromBase64 } from "../constant/convert-image";

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

const useStyles = makeStyles((theme: Theme) => ({
  root: { margin: theme.spacing(2), width: "100%" },
  statusSum: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  sumIcon: { width: theme.spacing(25), height: theme.spacing(12.5) },
  activeColor: { fill: theme.palette.primary.light },
  deactiveColor: { fill: theme.palette.common.white },
  activeColorLabel: { fill: theme.palette.common.white },
  deactiveColorLabel: {
    fill: theme.palette.primary.main,
  },
  divTable: {
    padding: `0px ${theme.spacing(2)}px`,
    height: `calc(100% - 254px)`,
  },
  divExport: {
    padding: `0px ${theme.spacing(2)}px ${theme.spacing(2)}px`,
    display: "flex",
    justifyContent: "flex-end",
  },
  container: {
    height: `calc(100% - 52px)`,
  },
  divAction: {
    display: "flex",
    justifyContent: "flex-end",
    padding: theme.spacing(2),
    paddingTop: theme.spacing(1),
  },
  btn: { textTransform: "unset" },
  exporticon: { fontSize: "2.25rem" },
}));

interface Column {
  id: "electricDevice" | "detail" | "workFunc" | "qty" | "comment";
  label: string;
  minWidth?: number;
  align?: "right" | "center";
  format?: (value: number) => string;
  maxWidth?: number;
}

const columns: Column[] = [
  { id: "electricDevice", label: "Electric Device", minWidth: 150 },
  { id: "detail", label: "Detail", minWidth: 120, align: "center" },
  {
    id: "workFunc",
    label: "Work Function",
    minWidth: 150,
    align: "center",
  },
  {
    id: "qty",
    label: "QTY",
    minWidth: 20,
    align: "right",
    format: (value: number) => value.toLocaleString("en-US"),
  },
  {
    id: "comment",
    label: "Comment",
    align: "right",
    minWidth: 120,
  },
];

interface Data {
  electricDevice: string;
  detail: string | number;
  detailLabel?: string;
  workFunc: string;
  qty: number;
  comment: string;
}

function compareValues(key: string, order = "asc") {
  return function innerSort(a: any, b: any) {
    if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) {
      // property doesn't exist on either object
      return 0;
    }

    const varA = typeof a[key] === "string" ? a[key].toUpperCase() : a[key];
    const varB = typeof b[key] === "string" ? b[key].toUpperCase() : b[key];

    let comparison = 0;
    if (varA > varB) {
      comparison = 1;
    } else if (varA < varB) {
      comparison = -1;
    }
    return order === "desc" ? comparison * -1 : comparison;
  };
}

const EquipSummary = () => {
  const classes = useStyles();
  const { event, status, setstatus } = useContext(DesignContext);
  // const [status, setstatus] = useState<number>(0);
  const [equipData, setequipData] = useState<Data[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { map } = useContext(DesignContext);
  const { userInfo } = useContext(MainContext);
  const { lang } = useContext(LangContext);
  const [loading, setloading] = useState<boolean>(false);
  const { state } = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const init = useCallback(async () => {
    const jsonState = JSON.parse(String(state));
    const convertDesignEquipToSummary = () => {
      let equipSummary = new Array(0);
      event.designEquips.map((dE: any) => {
        const deProperty = dE.source.data.properties;
        if (deProperty.equip_type === "equip-pole") {
          const checkHasPole = equipSummary.findIndex(
            (eSum: any) =>
              eSum.electricDevice === "Pole" &&
              eSum.detail === deProperty.poleSize &&
              eSum.workFunc === deProperty.workFunc
          );

          if (Boolean(checkHasPole >= 0)) {
            equipSummary[checkHasPole] = {
              ...equipSummary[checkHasPole],
              qty: equipSummary[checkHasPole].qty + 1,
            };
          } else {
            equipSummary = [
              ...equipSummary,
              {
                electricDevice: "Pole",
                detail: deProperty.poleSize,
                detailLabel: PoleSizeList.find(
                  (ps: any) => ps.value === deProperty.poleSize
                )?.label,
                workFunc: deProperty.workFunc,
                qty: 1,
                comment: deProperty.comment ? deProperty.comment : "-",
              },
            ];
          }
          if (deProperty.lamp) {
            const checkHasLamp = equipSummary.findIndex(
              (eSum: any) =>
                eSum.electricDevice === "Lamp" &&
                eSum.detail === deProperty.lampPower &&
                eSum.workFunc === deProperty.workFunc
            );
            if (Boolean(checkHasLamp >= 0)) {
              equipSummary[checkHasLamp] = {
                ...equipSummary[checkHasLamp],
                qty: equipSummary[checkHasLamp].qty + 1,
              };
            } else {
              equipSummary = [
                ...equipSummary,
                {
                  electricDevice: "Lamp",
                  detail: deProperty.lampPower,
                  detailLabel: LampPowerList.find(
                    (lp: any) => lp.value === deProperty.lampPower
                  )?.label,
                  workFunc: deProperty.workFunc,
                  qty: 1,
                  comment: deProperty.comment ? deProperty.comment : "-",
                },
              ];
            }
          }
          if (deProperty.ground) {
            const checkHasGround = equipSummary.findIndex(
              (eSum: any) =>
                eSum.electricDevice === "Ground" &&
                eSum.workFunc === deProperty.workFunc
            );
            if (Boolean(checkHasGround >= 0)) {
              equipSummary[checkHasGround] = {
                ...equipSummary[checkHasGround],
                qty: equipSummary[checkHasGround].qty + 1,
              };
            } else {
              equipSummary = [
                ...equipSummary,
                {
                  electricDevice: "Ground",
                  detail: "-",
                  workFunc: deProperty.workFunc,
                  qty: 1,
                  comment: deProperty.comment ? deProperty.comment : "-",
                },
              ];
            }
          }
        } else if (deProperty.equip_type === "equip-cable") {
          const checkHasCable = equipSummary.findIndex(
            (eSum: any) =>
              eSum.electricDevice === "Cable" &&
              eSum.detail === deProperty.cableType &&
              eSum.workFunc === deProperty.workFunc
          );

          if (Boolean(checkHasCable >= 0)) {
            equipSummary[checkHasCable] = {
              ...equipSummary[checkHasCable],
              qty: equipSummary[checkHasCable].qty + 1,
            };
          } else {
            equipSummary = [
              ...equipSummary,
              {
                electricDevice: "Cable",
                detail: deProperty.cableType,
                detailLabel: CableTypeList.find(
                  (ctl: any) => ctl.value === deProperty.cableType
                )?.label,
                workFunc: deProperty.workFunc,
                qty: 1,
                comment: deProperty.comment ? deProperty.comment : "-",
              },
            ];
          }
        } else if (deProperty.equip_type === "equip-relay") {
          const checkHas = equipSummary.findIndex(
            (eSum: any) =>
              eSum.electricDevice === "Relay" &&
              eSum.detail === deProperty.relayType &&
              eSum.workFunc === deProperty.workFunc
          );

          if (Boolean(checkHas >= 0)) {
            equipSummary[checkHas] = {
              ...equipSummary[checkHas],
              qty: equipSummary[checkHas].qty + 1,
            };
          } else {
            equipSummary = [
              ...equipSummary,
              {
                electricDevice: "Relay",
                detail: deProperty.relayType,
                detailLabel: RelayTypeList.find(
                  (rt: any) => rt.value === deProperty.relayType
                )?.label,
                workFunc: deProperty.workFunc,
                qty: 1,
                comment: deProperty.comment ? deProperty.comment : "-",
              },
            ];
          }
        } else if (deProperty.equip_type === "equip-transformer") {
          const checkHas = equipSummary.findIndex(
            (eSum: any) =>
              eSum.electricDevice === "Transformer" &&
              eSum.workFunc === deProperty.workFunc
          );

          if (Boolean(checkHas >= 0)) {
            equipSummary[checkHas] = {
              ...equipSummary[checkHas],
              qty: equipSummary[checkHas].qty + 1,
            };
          } else {
            equipSummary = [
              ...equipSummary,
              {
                electricDevice: "Transformer",
                detail: "-",
                workFunc: deProperty.workFunc,
                qty: 1,
                comment: deProperty.comment ? deProperty.comment : "-",
              },
            ];
          }
        } else if (deProperty.equip_type === "equip-switch") {
          const checkHas = equipSummary.findIndex(
            (eSum: any) =>
              eSum.electricDevice === "Switch" &&
              eSum.detail === deProperty.switchType &&
              eSum.workFunc === deProperty.workFunc
          );

          if (Boolean(checkHas >= 0)) {
            equipSummary[checkHas] = {
              ...equipSummary[checkHas],
              qty: equipSummary[checkHas].qty + 1,
            };
          } else {
            equipSummary = [
              ...equipSummary,
              {
                electricDevice: "Switch",
                detail: deProperty.switchType,
                detailLabel: SwitchTypeList.find(
                  (st: any) => st.value === deProperty.switchType
                )?.label,
                workFunc: deProperty.workFunc,
                qty: 1,
                comment: deProperty.comment ? deProperty.comment : "-",
              },
            ];
          }
        } else if (deProperty.equip_type === "equip-meter") {
          const checkHas = equipSummary.findIndex(
            (eSum: any) =>
              eSum.electricDevice === "Meter" &&
              eSum.detail === deProperty.meterType &&
              eSum.workFunc === deProperty.workFunc
          );

          if (Boolean(checkHas >= 0)) {
            equipSummary[checkHas] = {
              ...equipSummary[checkHas],
              qty: equipSummary[checkHas].qty + 1,
            };
          } else {
            equipSummary = [
              ...equipSummary,
              {
                electricDevice: "Meter",
                detail: deProperty.meterType,
                detailLabel: MeterTypeList.find(
                  (mt: any) => mt.value === deProperty.meterType
                )?.label,
                workFunc: deProperty.workFunc,
                qty: 1,
                comment: deProperty.comment ? deProperty.comment : "-",
              },
            ];
          }
        } else {
          const checkHas = equipSummary.findIndex(
            (eSum: any) =>
              eSum.electricDevice === "Riser-Transformer" &&
              eSum.workFunc === deProperty.workFunc
          );

          if (Boolean(checkHas >= 0)) {
            equipSummary[checkHas] = {
              ...equipSummary[checkHas],
              qty: equipSummary[checkHas].qty + 1,
            };
          } else {
            equipSummary = [
              ...equipSummary,
              {
                electricDevice: "Riser-Transformer",
                detail: "-",
                workFunc: deProperty.workFunc,
                qty: 1,
                comment: deProperty.comment ? deProperty.comment : "-",
              },
            ];
          }
        }
        return null;
      });
      return equipSummary
        ? equipSummary.sort(compareValues("electricDevice"))
        : equipSummary;
    };

    const proj = await getProjectById(jsonState._id);
    if (event.designEquips.length) {
      const summaryEquip = convertDesignEquipToSummary();
      setequipData(summaryEquip);
    }

    setstatus(proj.data[0].status);
  }, [event, state, setequipData]);

  useEffect(() => {
    init();
    return () => {};
  }, [init]);

  const _handleChangeStatus = async () => {
    setloading(true);
    const jsonState = JSON.parse(String(state));
    const nextStatus: number =
      status === 0 ? 1 : status === 1 ? 2 : status === 2 ? 1 : 0;
    const updateProject: IProject = {
      ...jsonState,
      designs: event.designEquips,
      status: nextStatus,
    };
    const rsUpdateProj = await putEditProject(updateProject);
    if (rsUpdateProj?.status?.code === 200) {
      if (nextStatus === 2) {
        const bboxArray: any = map?.getBounds().toArray();
        let img = map?.getCanvas().toDataURL();
        const imgBlob = makeBlobFromBase64(img);
        let fd = new FormData();
        fd.append("file", imgBlob, `${jsonState._id}.png`);
        const rsUploadFile = await uploadFile(fd);
        if (rsUploadFile.data.data[0].file) {
          const url = String(
            process.env.NODE_ENV === "production"
              ? `${process.env.REACT_APP_REANDER_UPLOAD_FILE}${rsUploadFile.data.data[0].file}`
              : rsUploadFile.data.data[0].file
          );
          await saveProjectFile(String(updateProject._id), {
            url,
            bbox: [
              bboxArray[0][0],
              bboxArray[1][1],
              bboxArray[1][0],
              bboxArray[0][1],
            ],
          });
        }
      }
      enqueueSnackbar("Project Saved.", {
        variant: "success",
      });
      setstatus(nextStatus);
      setloading(false);
    } else {
      enqueueSnackbar("Opp!! Something wrong.", {
        variant: "error",
      });
      setloading(false);
    }
  };

  const _sendEmail = async () => {
    setloading(true);
    const jsonState = JSON.parse(String(state));
    const contentMail = { ...jsonState, ...userInfo };
    const userHeadList = await getHeadUserList(contentMail.department_id);
    if (userHeadList.data) {
      const userHeadMail = userHeadList.data
        .filter(
          (user: any) =>
            user.mea.sec_short_name && user.mea.sec_short_name !== "อป."
        )
        .map((user: any) => user.email);
      if (userHeadMail.length > 0) {
        const bodyMail = projectComplete(contentMail);
        const rsSendMail = await sendEmail(userHeadMail, bodyMail);
        if (rsSendMail.data) {
          enqueueSnackbar("Send email success.", {
            variant: "success",
          });
          setloading(false);
        } else {
          enqueueSnackbar("Opp!! Something wrong.", {
            variant: "error",
          });
          setloading(false);
        }
      } else {
        enqueueSnackbar("ไม่พบ email ของ หน.กจ. หรือ หน.บร.", {
          variant: "warning",
        });
        setloading(false);
      }
    } else {
      enqueueSnackbar("Opp!! Something wrong.", {
        variant: "error",
      });
      setloading(false);
    }
    //
  };

  return (
    <Paper className={classes.root}>
      <div className={classes.statusSum}>
        <SummaryInprocess
          className={clsx(
            classes.sumIcon,
            status >= 1 ? classes.activeColor : classes.deactiveColor
          )}
        >
          <SummaryInprocessLabel
            className={clsx(
              status >= 1
                ? classes.activeColorLabel
                : classes.deactiveColorLabel
            )}
          />
        </SummaryInprocess>
        <SummaryComplete
          className={clsx(
            classes.sumIcon,
            status === 2 ? classes.activeColor : classes.deactiveColor
          )}
        >
          <SummaryCompleteLabel
            className={clsx(
              status === 2
                ? classes.activeColorLabel
                : classes.deactiveColorLabel
            )}
          />
        </SummaryComplete>
      </div>
      <div className={classes.divExport}>
        <ExcelFile
          filename={`เลขรับที่ ${
            JSON.parse(String(state)).work_order_no
              ? JSON.parse(String(state)).work_order_no
              : "-"
          } ${
            JSON.parse(String(state)).department_name
              ? JSON.parse(String(state)).department_name
              : "-"
          }`}
          element={
            <IconButton size="small">
              <ExportExcel className={classes.exporticon} />
            </IconButton>
          }
        >
          <ExcelSheet data={equipData} name="Summary">
            <ExcelColumn label="Electric Device" value="electricDevice" />
            <ExcelColumn label="Detail" value={"detailLabel"} />
            <ExcelColumn label="Work Fuction" value="workFunc" />
            <ExcelColumn label="QTY" value="qty" />
            <ExcelColumn label="Comment" value="comment" />
          </ExcelSheet>
        </ExcelFile>
      </div>
      <div className={classes.divTable}>
        <TableContainer className={classes.container}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{
                      minWidth: column.minWidth,
                      maxWidth: column.maxWidth,
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {equipData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row: Data, i: number) => {
                  return (
                    <TableRow hover role="checkbox" tabIndex={-1} key={i}>
                      <TableCell>{row.electricDevice}</TableCell>
                      <TableCell align="center">
                        {row.detailLabel ? row.detailLabel : row.detail}
                      </TableCell>
                      <TableCell align="center">{row.workFunc}</TableCell>
                      <TableCell align="right">
                        {row.qty.toLocaleString("US")}
                      </TableCell>
                      <TableCell align="right">
                        {row.comment ? row.comment : "-"}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={equipData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </div>
      <div className={classes.divAction}>
        {userInfo?.role === "officer_head" && status === 2 && (
          <Button
            className={classes.btn}
            variant="contained"
            color="secondary"
            onClick={_sendEmail}
            disabled={loading}
          >
            {loading && (
              <CircularProgress
                color="primary"
                style={{ marginRight: 4 }}
                size={24}
              />
            )}
            {lang.data.sendEmailReport}
          </Button>
        )}
        <Button
          className={classes.btn}
          style={{ marginLeft: 16 }}
          variant="contained"
          color="primary"
          disabled={
            !Boolean(JSON.parse(String(state)).responsible_person) || loading
          }
          onClick={_handleChangeStatus}
        >
          {status === 0 || status === 2
            ? lang.data.inProcess
            : lang.data.complete}
        </Button>
      </div>
    </Paper>
  );
};

export default EquipSummary;
